import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Space, Spin} from "antd";
import {DeleteOutlined} from "@ant-design/icons";

// «Вид из окна» as on ECI old and the portal: per window two points on a Yandex map — the window, then where it looks —
// drawn as a 20° sector (the owner, 23.10: «люди ставят две точки — начало и конец, рисуется равнобедренный
// треугольник»). The value is ECI old's: [[lat1, lon1, lat2, lon2], …] here, { "0": […] } on the server.

// the portal's public JS API key (ECI_frontend_agent entities/map/lib/apiKey.js)
const API_KEY = 'cb9e8401-af28-428c-8326-1ceab5bf1851'
const SPB = [59.9386, 30.3141]
const FILL = '#0f59cae3'

let ymapsPromise = null
const loadYmaps = () => {
  if (!ymapsPromise) {
    ymapsPromise = new Promise((resolve, reject) => {
      if (window.ymaps) {
        window.ymaps.ready(() => resolve(window.ymaps))
        return
      }
      const script = document.createElement('script')
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${API_KEY}&lang=ru_RU`
      script.async = true
      script.onload = () => window.ymaps.ready(() => resolve(window.ymaps))
      script.onerror = () => {
        ymapsPromise = null
        reject(new Error('Яндекс.Карты не загрузились'))
      }
      document.head.appendChild(script)
    })
  }
  return ymapsPromise
}

// a typed address → [lat, lon] (Yandex geocoder, the first match), null when it finds nothing
export const geocode = async (address) => {
  const ymaps = await loadYmaps()
  const result = await ymaps.geocode(address, {results: 1})
  const first = result.geoObjects.get(0)
  return first ? first.geometry.getCoordinates() : null
}

// the sector of one arrow, as the portal draws it (getViewFromTheWindowCoordinates): [[lat, lon] × 3]
export const sectorOf = ([lat1, lon1, lat2, lon2]) => {
  const beta = 10 / (180 / Math.PI)
  const [y1, x1, y2, x2] = [lat1, lon1, lat2, lon2].map(Number)
  let alpha = Math.atan((y2 - y1) / (x2 - x1))
  const a = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) / Math.cos(beta)
  if (x2 < x1 && y1 < y2) alpha = Math.PI - Math.abs(alpha)
  if (x2 < x1 && y2 < y1) alpha = Math.abs(alpha) - Math.PI
  if (x1 < x2 && y2 < y1) alpha = 2 * Math.PI - Math.abs(alpha)
  const k = Math.cos(y1 / (180 / Math.PI))
  return [
    [y1, x1],
    [y1 + Math.sin(alpha - beta) * a, x1 + (Math.cos(alpha - beta) * a) / k],
    [y1 + Math.sin(alpha + beta) * a, x1 + (Math.cos(alpha + beta) * a) / k],
  ]
}

const round = (n) => Math.round(n * 1e7) / 1e7

// the drag points: small circles, 12 px (the owner, 23.10: «точку чуть поменьше»); the window filled, the far point hollow
const HANDLE = 12
const handleIcon = (fill, stroke) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="${HANDLE}" height="${HANDLE}"><circle cx="${HANDLE / 2}" cy="${HANDLE / 2}" r="${HANDLE / 2 - 1.5}" fill="${fill}" stroke="${stroke}" stroke-width="2"/></svg>`,
)}`
const WINDOW_ICON = handleIcon('#0f59ca', '#ffffff')
const TIP_ICON = handleIcon('#ffffff', '#0f59ca')

// The formula stretches the sector along the longitude by 1/cos(latitude) (about ×2 in Petersburg): the middle of its
// far side is not the stored second point but (lat2, lon1 + (lon2 − lon1) / cos(lat1)). The editor keeps the drawing as
// the portal's and puts the stored point so that the far side's middle lands where the user clicked or dragged.
const stretch = (lat) => Math.cos(lat / (180 / Math.PI))
// where the far side's middle is drawn
const tipOf = ([lat1, lon1, lat2, lon2]) => [lat2, lon1 + (lon2 - lon1) / stretch(lat1)]
// the arrow to store for a window point and a far-side middle
const arrowTo = ([lat1, lon1], [latT, lonT]) => [lat1, lon1, latT, lon1 + (lonT - lon1) * stretch(lat1)]

export const ViewArrowsMap = ({value, onChange, center, disabled}) => {
  const box = useRef(null)
  const map = useRef(null)
  const layer = useRef(null)
  const ymapsRef = useRef(null)
  const [start, setStart] = useState(null)
  const [error, setError] = useState(null)
  const [ready, setReady] = useState(false)
  // the first drawing shows every arrow; later the map stays where the user put it
  const framed = useRef(false)
  const arrows = Array.isArray(value) ? value : []
  // the click handler is set once; it reads the latest state through these
  const state = useRef({arrows, start, disabled})
  state.current = {arrows, start, disabled}

  useEffect(() => {
    let alive = true
    loadYmaps()
      .then((ymaps) => {
        if (!alive || !box.current) return
        ymapsRef.current = ymaps
        map.current = new ymaps.Map(box.current, {
          center: center || SPB,
          zoom: center ? 17 : 11,
          controls: ['zoomControl', 'fullscreenControl'],
        }, {suppressMapOpenBlock: true})
        // the wheel scrolls the form, not the map (zoom with + and −)
        map.current.behaviors.disable('scrollZoom')
        layer.current = new ymaps.GeoObjectCollection()
        map.current.geoObjects.add(layer.current)
        map.current.events.add('click', (e) => {
          const {arrows: current, start: from, disabled: off} = state.current
          if (off) return
          const [lat, lon] = e.get('coords').map(round)
          if (!from) {
            setStart([lat, lon])
            return
          }
          if (from[0] === lat && from[1] === lon) return
          setStart(null)
          onChange([...current, arrowTo(from, [lat, lon]).map(round)])
        })
        setReady(true)
      })
      .catch((e) => alive && setError(e.message))
    return () => {
      alive = false
      if (map.current) map.current.destroy()
      map.current = null
    }
    // the map is made once per editor; arrows and the start point are drawn by the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // a flat without coordinates gets its complex's once the complex is chosen: the map moves there
  const centerKey = center ? center.join(',') : ''
  useEffect(() => {
    if (ready && map.current && center) map.current.setCenter(center, 17)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, centerKey])

  // draw the sectors, the flat and a pending start point
  useEffect(() => {
    const ymaps = ymapsRef.current
    if (!ready || !ymaps || !layer.current) return
    layer.current.removeAll()
    if (center) {
      layer.current.add(new ymaps.Placemark(center, {hintContent: 'Квартира (по координатам)'}, {preset: 'islands#redDotIcon', interactivityModel: 'default#transparent'}))
    }
    // every sector can be dragged whole, its window point moved, its far point turned and stretched (the owner, 23.10:
    // «схватить, потянуть, подвинуть направление, уменьшить, увеличить»); the sector follows while dragging
    const replace = (index, arrow) => onChange(state.current.arrows.map((a, i) => (i === index ? arrow.map(round) : a)))
    arrows.forEach((arrow, i) => {
      const off = state.current.disabled
      const sector = new ymaps.Polygon([sectorOf(arrow)], {hintContent: off ? `Окно ${i + 1}` : `Окно ${i + 1}: потяните, чтобы передвинуть`}, {
        fillColor: FILL, strokeWidth: 0, draggable: !off, cursor: off ? 'arrow' : 'move',
        ...(off ? {interactivityModel: 'default#transparent'} : {}),
      })
      layer.current.add(sector)
      if (off) return
      // the whole sector: both points move by the same step as its apex
      sector.events.add('dragend', () => {
        const [lat, lon] = sector.geometry.getCoordinates()[0][0]
        const dLat = lat - arrow[0]
        const dLon = lon - arrow[1]
        replace(i, [arrow[0] + dLat, arrow[1] + dLon, arrow[2] + dLat, arrow[3] + dLon])
      })
      const handle = (coords, hint, icon, pointOf) => {
        const mark = new ymaps.Placemark(coords, {hintContent: hint}, {
          draggable: true, iconLayout: 'default#image', iconImageHref: icon,
          iconImageSize: [HANDLE, HANDLE], iconImageOffset: [-HANDLE / 2, -HANDLE / 2],
        })
        mark.events.add('drag', () => sector.geometry.setCoordinates([sectorOf(pointOf(mark.geometry.getCoordinates()))]))
        mark.events.add('dragend', () => replace(i, pointOf(mark.geometry.getCoordinates())))
        layer.current.add(mark)
      }
      // moving the window keeps the far side where it is; moving the far point turns and stretches the sector
      const tip = tipOf(arrow)
      handle([arrow[0], arrow[1]], `Окно ${i + 1}: потяните, чтобы передвинуть окно`, WINDOW_ICON,
        (point) => arrowTo(point, tip))
      handle(tip, `Окно ${i + 1}: потяните, чтобы повернуть и изменить длину`, TIP_ICON,
        (point) => arrowTo([arrow[0], arrow[1]], point))
    })
    if (start) {
      layer.current.add(new ymaps.Placemark(start, {hintContent: 'Окно: теперь кликните, куда смотрит вид'}, {
        preset: 'islands#blueCircleDotIcon', interactivityModel: 'default#transparent',
      }))
    }
    if (!framed.current && arrows.length) {
      framed.current = true
      const bounds = layer.current.getBounds()
      if (bounds) map.current.setBounds(bounds, {checkZoomRange: true, zoomMargin: 40}).then(() => {
        if (map.current && map.current.getZoom() > 18) map.current.setZoom(18)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, arrows, start, centerKey])

  const removeArrow = (index) => onChange(arrows.filter((_, i) => i !== index))

  return (
    <div className='view-arrows'>
      {error && <Alert type='error' showIcon message={`${error}. Обновите страницу.`}/>}
      <p className='cell-hint'>
        {disabled
          ? 'Вашим ролям менять вид из окна нельзя.'
          : start
            ? 'Окно отмечено. Теперь кликните, куда смотрит вид из этого окна.'
            : 'Кликните на карте у окна квартиры, затем — куда смотрит вид. Для каждого окна — своя пара точек. Готовый треугольник можно перетащить целиком, а за точки — передвинуть окно, повернуть и изменить длину.'}
      </p>
      <div className='view-arrows__map' ref={box}>{!ready && !error && <Spin/>}</div>
      <Space wrap className='view-arrows__list'>
        {arrows.map((_, i) => (
          <Button key={i} size='small' icon={<DeleteOutlined/>} disabled={disabled} onClick={() => removeArrow(i)}>Окно {i + 1}</Button>
        ))}
        {start && <Button size='small' onClick={() => setStart(null)}>Отменить начатое</Button>}
        {arrows.length > 1 && !disabled && <Button size='small' danger onClick={() => onChange([])}>Убрать все</Button>}
        {!arrows.length && !start && <span className='cell-empty'>Вид не отмечен</span>}
      </Space>
    </div>
  )
}
